import React, { useMemo, useRef, useEffect } from 'react'
import { pathOr, isNil } from 'ramda'

import PropTypes from 'prop-types'
import Spin from 'components/SpinA11y'
import { useQuery } from '@apollo/client'

import PROVIDERS_PAGE from 'apollo/queries/PROVIDERS_PAGE'
import Provider from 'model/provider/provider'
import { initArray } from 'model/helpers'

import { NODES_PATH } from 'constants/dataPaths'
import { verifiedValues } from 'components/VerifiedDropdown/helpers'
import NoResults from 'components/NoResults'
import { convertVerifiedAfterToSeconds } from 'utils/helpers/filtersHelper'
import useScroll from 'utils/hooks/useScroll'
import { TableWrap } from 'ui/entities/styled'
import { parseNonZeroJson } from 'utils/strings'

import ProviderItem from './ProviderItem'

const ProvidersTable = ({
  filterValues,
  onSelectEntity = () => {},
  onRemoveEntity = () => {},
  entitiesIDs = [],
  setMarkers = () => {},
  setMarkersLoading = () => {},
  isCarePlan = false,
  isStandalonePage = false,
  isSelectionMode = false,
  employee_id
}) => {
  const ref = useRef()
  const {
    distance,
    npi,
    name,
    specialties_uuids,
    gender,
    language,
    address,
    min_rating,
    plan_id,
    is_verified,
    min_confidence,
    verified_after,
    insurance_plan_tiers
  } = filterValues

  const distanceNum = distance ? parseInt(distance, 10) : 20

  const verifiedAfter = useMemo(() => {
    const date = verifiedValues.find(
      item => item.value === verified_after
    )?.subtract
    return convertVerifiedAfterToSeconds(date)
  }, [verified_after])

  const hasPlanId = !!plan_id
  const hasTiers = !!insurance_plan_tiers && !!insurance_plan_tiers?.length
  const minConfidence = isNil(min_confidence)
    ? null
    : parseInt(min_confidence, 10)

  const variables = {
    member_has_plan: hasPlanId,
    ...(hasPlanId && { member_plan_id: plan_id }),
    employee_id: !!employee_id ? employee_id : undefined,
    filters: {
      min_location_confidence: minConfidence,
      ...(npi && { npi: [npi] }),
      ...(name && { name }),
      is_verified,
      verified_after: verifiedAfter,
      specialties_uuids: parseNonZeroJson(specialties_uuids),
      distance: !!address ? distanceNum : undefined,
      gender,
      language,
      address_in_one_line: address,
      min_rating: min_rating ? +min_rating : undefined,
      ...(hasPlanId &&
        hasTiers && {
          insurance_plan_tiers: {
            kinds: JSON.parse(insurance_plan_tiers),
            plan_id
          }
        })
    }
  }

  const { data, loading, fetchMore } = useQuery(PROVIDERS_PAGE, {
    variables,
    errorPolicy: 'all'
  })
  const pageInfo = pathOr(
    '',
    ['ribbon_health_doctors_without_defaults_v2', 'pageInfo'],
    data
  )
  const dataPath = ['ribbon_health_doctors_without_defaults_v2']

  const providers = initArray(data, [...dataPath, NODES_PATH], Provider)

  const { scrollLoading } = useScroll({
    dataPath,
    dataType: NODES_PATH,
    fetchMore,
    pageInfo,
    scrollRef: ref,
    filterUniqueIds: true
  })

  useEffect(() => {
    setMarkersLoading(loading)
    if (providers) {
      const definedMarkers = []
      for (let i = 0; i < providers.length; i++) {
        const addresses = providers[i]?.addresses
        const { id, full_name } = providers[i]
        if (
          addresses &&
          addresses.length &&
          addresses[0].latitude &&
          addresses[0].longitude
        ) {
          definedMarkers.push({
            id,
            latitude:
              addresses && addresses.length ? addresses[0].latitude : false,
            longitude:
              addresses && addresses.length ? addresses[0].longitude : false,
            name: full_name
          })
        }
      }

      setMarkers(definedMarkers)
    }
    // eslint-disable-next-line
  }, [loading, JSON.stringify(providers), setMarkers, setMarkersLoading])

  return (
    <Spin name="providersTable" spinning={loading || scrollLoading}>
      <TableWrap
        ref={ref}
        customOffset={!isStandalonePage ? '530' : 'false'}
        data-test="providersTable"
      >
        {providers.map((provider, index) => (
          <ProviderItem
            key={provider.id}
            provider={provider}
            index={index + 1}
            isSelected={entitiesIDs.includes(provider.id)}
            onSelectEntity={onSelectEntity}
            onRemoveEntity={onRemoveEntity}
            isCarePlan={isCarePlan}
            isSelectionMode={isSelectionMode}
            addButtonText={
              isStandalonePage ? 'Add To Care Plan Draft' : 'Add To Care Plan'
            }
          />
        ))}
        {providers.length === 0 && !loading && <NoResults />}
      </TableWrap>
    </Spin>
  )
}

ProvidersTable.propTypes = {
  filterValues: PropTypes.object.isRequired,
  onSelectEntity: PropTypes.func,
  onRemoveEntity: PropTypes.func,
  entitiesIDs: PropTypes.array,
  isStandalonePage: PropTypes.bool,
  isSelectionMode: PropTypes.bool,
  isCarePlan: PropTypes.bool,
  setMarkers: PropTypes.func,
  setMarkersLoading: PropTypes.func
}

export default ProvidersTable
